import React, { useState, useMemo } from "react";
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Typography,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import menus from "../../utils/menu";

interface MenuItem {
  label: string;
  path: string;
  icon?: React.ReactNode;
  subMenu?: MenuItem[];
}

interface SidebarProps {
  onMenuClick?: (menu?: MenuItem) => void;
  collapsed?: boolean;
}

function SidebarItem({
  menu,
  onMenuClick,
  collapsed,
}: {
  menu: MenuItem;
  onMenuClick?: (menu?: MenuItem) => void;
  collapsed?: boolean;
}) {
  const location = useLocation();

  const isActive = useMemo(() => {
    if (!menu.path) return false;
    return location.pathname === menu.path || location.pathname.startsWith(menu.path + '/');
  }, [location.pathname, menu.path]);

  const [open, setOpen] = useState(isActive);

  const handleClick = () => {
    if (collapsed) {
      if (!menu.subMenu && onMenuClick) {
        onMenuClick(menu);
      }
      return;
    }

    if (menu.subMenu) {
      setOpen((prev) => !prev);
    } else {
      onMenuClick?.(menu);
    }
  };

  const activeStyle = {
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    color: "#ffffff",
    borderRadius: "8px",
    "& .MuiListItemIcon-root": {
      color: "#ffffff",
    },
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.12)",
    }
  };

  const inactiveStyle = {
    color: "#9ca3af",
    borderRadius: "8px",
    "& .MuiListItemIcon-root": {
      color: "#9ca3af",
    },
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.04)",
      color: "#ffffff",
      "& .MuiListItemIcon-root": {
        color: "#ffffff",
      },
    }
  };

  return (
    <>
      <ListItemButton
        component={menu.subMenu && !collapsed ? "div" : Link}
        to={!menu.subMenu ? menu.path : undefined}
        onClick={handleClick}
        sx={{
          py: 1,
          px: collapsed ? 1.5 : 2,
          mb: 0.5,
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          transition: "all 0.2s ease",
          textDecoration: "none",
          ...(isActive ? activeStyle : inactiveStyle)
        }}
      >
        <ListItemIcon
          sx={{
            color: "inherit",
            minWidth: collapsed ? "auto" : 40,
            justifyContent: "center",
          }}
        >
          {menu.icon}
        </ListItemIcon>

        {!collapsed && (
          <ListItemText
            primary={menu.label}
            primaryTypographyProps={{
              fontSize: '0.95rem',
              fontWeight: isActive ? 600 : 500,
            }}
          />
        )}

        {menu.subMenu && !collapsed && (open ? <ExpandLess /> : <ExpandMore />)}
      </ListItemButton>

      {menu.subMenu && !collapsed && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ mt: 0.5, mb: 0.5 }}>
            {menu.subMenu.map((subItem) => {
              const subPath = `${menu.path}${subItem.path}`;
              const isSubActive = location.pathname === subPath || location.pathname.startsWith(subPath + '/');

              return (
                <ListItemButton
                  key={subPath}
                  component={Link}
                  to={subPath}
                  onClick={() => onMenuClick?.(subItem)}
                  sx={{
                    py: 1,
                    pl: 4,
                    pr: 2,
                    ml: 1.5,
                    mb: 0.5,
                    transition: "all 0.2s ease",
                    textDecoration: "none",
                    ...(isSubActive ? activeStyle : inactiveStyle)
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: "inherit",
                      minWidth: 36,
                    }}
                  >
                    {subItem.icon}
                  </ListItemIcon>

                  <ListItemText
                    primary={subItem.label}
                    primaryTypographyProps={{
                      fontSize: '0.85rem',
                      fontWeight: isSubActive ? 600 : 500,
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Collapse>
      )}
    </>
  );
}

function Sidebar({ onMenuClick, collapsed = false }: SidebarProps) {
  return (
    <Box
      sx={{
        width: "100%",
        p: 2,
        height: "100%",
        bgcolor: "#1A202C", // Dark background
        color: "#ffffff",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Brand / Logo Area */}
      <Box
        sx={{
          p: collapsed ? 1 : 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 1.5,
          mb: 1.5,
          pb: 2,
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        <Box
          component="img"
          src="/mtrcb-admin-logo.png"
          alt="MTRCB Admin Logo"
          sx={{
            width: collapsed ? 36 : 42,
            height: 'auto',
            objectFit: 'contain',
            filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2))',
            flexShrink: 0,
          }}
        />
        {!collapsed && (
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: 16,
                letterSpacing: 0.5,
                color: '#ffffff',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
              }}
            >
              MTRCB <span style={{ color: '#00AEEF', fontWeight: 700 }}>ADMIN</span>
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.55)',
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: 0.6,
                textTransform: 'uppercase',
                display: 'block',
              }}
            >
              App Portal
            </Typography>
          </Box>
        )}
      </Box>

      {/* Menu Subtitle */}
      {!collapsed && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600, letterSpacing: 1 }}>
            MENU
          </Typography>
        </Box>
      )}

      <Box sx={{ overflow: "auto", flexGrow: 1 }}>
        <List sx={{ p: 0 }}>
          {menus.map((menu, index) => (
            <SidebarItem
              key={`${menu.path}-${index}`}
              menu={menu}
              onMenuClick={onMenuClick}
              collapsed={collapsed}
            />
          ))}
        </List>
      </Box>
    </Box>
  );
}

export default Sidebar;